import { createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import { CommonHash } from 'app/types';

// Define a type for the slice state
export interface UserState {
  favorites?: Array<number>;
  nextSteps?: Array<string>;
  profileCompleted?: boolean;
  preferencesFilled?: boolean;
  meetingScheduled?: boolean;
  listOfProgramsAsked?: boolean;
  schoolShortlisted?: boolean;
  applicationStarted?: boolean;
  cvUploaded?: boolean;
  testsScoresUploaded?: boolean;
  applicationSubmitted?: boolean;
  profileProgress: CommonHash;
  lastStepDone: number;
}

// Define the initial state using that type
const initialState: UserState = {
  favorites: [],
  nextSteps: [
    'Complete your profile',
    'Fill your preferences',
    'Schedule a meeting with an expert',
    'Ask a list of programs from an expert',
    'Add at least one program to your shortlist',
    'Start an application',
    'Upload your CV',
    'Upload your Test Scores',
    'Submit an application',
  ],
  profileCompleted: false,
  preferencesFilled: false,
  meetingScheduled: false,
  listOfProgramsAsked: false,
  schoolShortlisted: false,
  applicationStarted: false,
  cvUploaded: false,
  testsScoresUploaded: false,
  applicationSubmitted: false,
  profileProgress: {},
  lastStepDone: 0,
};
export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLastStepDone: (state: UserState, action: PayloadAction<number>) => {
      state.lastStepDone = action.payload;
    },
    setFavorites: (state: UserState, action: PayloadAction<Array<number> | undefined>) => {
      state.favorites = action.payload;
    },
    cleanFavorites: (state: UserState) => {
      state.favorites = [];
      setFavorites([]);
    },
    setProfileCompleted: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.profileCompleted = action.payload;
    },
    setPreferencesFilled: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.preferencesFilled = action.payload;
    },
    setMeetingScheduled: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.meetingScheduled = action.payload;
    },
    setListOfProgramsAsked: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.listOfProgramsAsked = action.payload;
    },
    setSchoolShortlisted: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.schoolShortlisted = action.payload;
    },
    setApplicationStarted: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.applicationStarted = action.payload;
    },
    setCvUploaded: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.cvUploaded = action.payload;
    },
    setTestsScoresUploaded: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.testsScoresUploaded = action.payload;
    },
    setApplicationSubmitted: (state: UserState, action: PayloadAction<boolean | undefined>) => {
      state.applicationSubmitted = action.payload;
    },
    cleanNextSteps: (state: UserState) => {
      state.profileCompleted = false;
      state.preferencesFilled = false;
      state.meetingScheduled = false;
      state.listOfProgramsAsked = false;
      state.schoolShortlisted = false;
      state.applicationStarted = false;
      state.cvUploaded = false;
      state.testsScoresUploaded = false;
      state.applicationSubmitted = false;
      state.lastStepDone = 0;
      state.profileProgress = {};
    },
    setProfileProgress: (state: UserState, action: PayloadAction<CommonHash>) => {
      state.profileProgress = action.payload;
    },
  },
});

export const {
  setFavorites,
  cleanFavorites,
  setProfileCompleted,
  setPreferencesFilled,
  setMeetingScheduled,
  setListOfProgramsAsked,
  setSchoolShortlisted,
  setApplicationStarted,
  setCvUploaded,
  setTestsScoresUploaded,
  setApplicationSubmitted,
  setProfileProgress,
  cleanNextSteps,
  setLastStepDone,
} = UserSlice.actions;

export default UserSlice.reducer;
